"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ParameterManager = void 0;
const viewer_shared_node_tree_1 = require("@shapediver/viewer.shared.node-tree");
const viewer_shared_services_1 = require("@shapediver/viewer.shared.services");
const viewer_shared_types_1 = require("@shapediver/viewer.shared.types");
const DrawingParameter_1 = require("../dto/DrawingParameter");
const FileParameter_1 = require("../dto/FileParameter");
const Parameter_1 = require("../dto/Parameter");
/**
 * Manager responsible for parameters.
 *
 * The manager is created by the SessionEngineCore and can be accessed
 * via the `parameterManager` property.
 */
class ParameterManager {
    constructor(sessionEngineCore) {
        this._logger = viewer_shared_services_1.Logger.instance;
        this._parameterValues = {};
        this._parameters = {};
        this._parameterHistory = [];
        this._parameterHistoryCall = false;
        this._parameterHistoryForward = [];
        this._sessionEngineCore = sessionEngineCore;
    }
    get ignoreUnknownParams() {
        return this._ignoreUnknownParams;
    }
    set ignoreUnknownParams(value) {
        this._ignoreUnknownParams = value;
    }
    get parameterHistory() {
        return this._parameterHistory;
    }
    get parameterHistoryCall() {
        return this._parameterHistoryCall;
    }
    set parameterHistoryForward(value) {
        this._parameterHistoryForward = value;
    }
    get parameterValues() {
        return this._parameterValues;
    }
    get parameters() {
        return this._parameters;
    }
    /**
     * Checks whether it is possible to go back in the parameter history.
     *
     * @returns True if it is possible to go back, false otherwise.
     */
    canGoBack() {
        // the first entry is always the one from the init call
        // all additional entries can be undone
        return this._parameterHistory.length > 1;
    }
    /**
     * Checks whether it is possible to go forward in the parameter history.
     *
     * @returns True if it is possible to go forward, false otherwise.
     */
    canGoForward() {
        return this._parameterHistoryForward.length > 0;
    }
    /**
     * Creates parameters from the session engine's response DTO.
     *
     * @param initialParameters
     * @returns
     */
    createParametersFromDto(initialParameters) {
        if (!this._sessionEngineCore.responseDto)
            return;
        const parameterSet = {};
        for (const parameterId in this._sessionEngineCore.responseDto
            .parameters) {
            if (this.parameters[parameterId])
                continue;
            this._sessionEngineCore.responseDto.parameters[parameterId].id =
                parameterId;
            switch (true) {
                case this._sessionEngineCore.responseDto.parameters[parameterId]
                    .type === viewer_shared_types_1.PARAMETER_TYPE.BOOL:
                    this.parameters[parameterId] = new Parameter_1.Parameter(this._sessionEngineCore.responseDto.parameters[parameterId], this._sessionEngineCore, this);
                    break;
                case this._sessionEngineCore.responseDto.parameters[parameterId]
                    .type === viewer_shared_types_1.PARAMETER_TYPE.COLOR:
                    this.parameters[parameterId] = new Parameter_1.Parameter(this._sessionEngineCore.responseDto.parameters[parameterId], this._sessionEngineCore, this);
                    break;
                case this._sessionEngineCore.responseDto.parameters[parameterId]
                    .type === viewer_shared_types_1.PARAMETER_TYPE.FILE:
                    this.parameters[parameterId] = new FileParameter_1.FileParameter(this._sessionEngineCore.responseDto.parameters[parameterId], this._sessionEngineCore, this);
                    break;
                case this._sessionEngineCore.responseDto.parameters[parameterId]
                    .type === viewer_shared_types_1.PARAMETER_TYPE.EVEN ||
                    this._sessionEngineCore.responseDto.parameters[parameterId]
                        .type === viewer_shared_types_1.PARAMETER_TYPE.FLOAT ||
                    this._sessionEngineCore.responseDto.parameters[parameterId]
                        .type === viewer_shared_types_1.PARAMETER_TYPE.INT ||
                    this._sessionEngineCore.responseDto.parameters[parameterId]
                        .type === viewer_shared_types_1.PARAMETER_TYPE.ODD:
                    this.parameters[parameterId] = new Parameter_1.Parameter(this._sessionEngineCore.responseDto.parameters[parameterId], this._sessionEngineCore, this);
                    break;
                case this._sessionEngineCore.responseDto.parameters[parameterId]
                    .type === viewer_shared_types_1.PARAMETER_TYPE.INTERACTION:
                    this.parameters[parameterId] =
                        this._sessionEngineCore.utilsManager.createInteractionParameter(this._sessionEngineCore.responseDto.parameters[parameterId]);
                    break;
                case this._sessionEngineCore.responseDto.parameters[parameterId]
                    .type === viewer_shared_types_1.PARAMETER_TYPE.DRAWING:
                    this.parameters[parameterId] = new DrawingParameter_1.DrawingParameter(this._sessionEngineCore.responseDto.parameters[parameterId], this._sessionEngineCore, this);
                    break;
                default:
                    this.parameters[parameterId] = new Parameter_1.Parameter(this._sessionEngineCore.responseDto.parameters[parameterId], this._sessionEngineCore, this);
                    break;
            }
            // we don't have to do larger restrictions for this as the backend would have already thrown an error if the values were not correct
            if (initialParameters) {
                let hasInitialValue = false;
                // check if the id is within the initial parameters
                if (initialParameters[parameterId] !== undefined) {
                    this.parameters[parameterId].value =
                        initialParameters[parameterId];
                    hasInitialValue = true;
                }
                // check if the name is within the initial parameters
                else if (initialParameters[this.parameters[parameterId].name] !==
                    undefined) {
                    this.parameters[parameterId].value =
                        initialParameters[this.parameters[parameterId].name];
                    hasInitialValue = true;
                }
                // check if the displayname is within the initial parameters
                else if (this.parameters[parameterId].displayname &&
                    initialParameters[this.parameters[parameterId].displayname] !== undefined) {
                    this.parameters[parameterId].value =
                        initialParameters[this.parameters[parameterId].displayname];
                    hasInitialValue = true;
                }
                // if there is an initial value, we try to cast it to the correct type
                // we only do this for bool and number types, as the other types are either string or have their own parsing
                if (hasInitialValue) {
                    if (this.parameters[parameterId].type ===
                        viewer_shared_types_1.PARAMETER_TYPE.BOOL) {
                        if (typeof this.parameters[parameterId].value ===
                            "string") {
                            this.parameters[parameterId].value =
                                this.parameters[parameterId].value.toLowerCase() === "true";
                        }
                    }
                    else if (this.parameters[parameterId].type ===
                        viewer_shared_types_1.PARAMETER_TYPE.INT ||
                        this.parameters[parameterId].type ===
                            viewer_shared_types_1.PARAMETER_TYPE.FLOAT ||
                        this.parameters[parameterId].type ===
                            viewer_shared_types_1.PARAMETER_TYPE.EVEN ||
                        this.parameters[parameterId].type === viewer_shared_types_1.PARAMETER_TYPE.ODD) {
                        // cast to number
                        this.parameters[parameterId].value = Number(this.parameters[parameterId].value);
                    }
                }
            }
            parameterSet[parameterId] = {
                value: this.parameters[parameterId].value,
                valueString: this.parameters[parameterId].stringify(),
            };
            if (!this._sessionEngineCore.initialized)
                this.parameterValues[parameterId] =
                    parameterSet[parameterId].valueString;
        }
        // store the initialization as the first parameter set in the history
        if (!this._sessionEngineCore.initialized)
            this.parameterHistory.push(parameterSet);
    }
    /**
     * Goes one step back in the parameter history and applies the parameter values.
     *
     * @returns The tree node returned by the customization function.
     */
    goBack() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.canGoBack()) {
                this._logger.debug(`Session(${this._sessionEngineCore.id}).goBack: Cannot go further back.`);
                return new viewer_shared_node_tree_1.TreeNode();
            }
            // get the current parameter set and store it in the forward history later on
            const currentParameterSet = this._parameterHistory.pop();
            // adjust the parameters according to the last parameter set
            const lastParameterSet = this._parameterHistory[this._parameterHistory.length - 1];
            for (const parameterId in lastParameterSet)
                this.parameters[parameterId].value =
                    lastParameterSet[parameterId].value;
            // call the customization function with the parameterHistoryCall value set to true
            this._parameterHistoryCall = true;
            const node = yield this._sessionEngineCore.customizationManager.customize();
            this._parameterHistoryCall = false;
            // add the current (not anymore current) parameter set to the forward history
            this._parameterHistoryForward.push(currentParameterSet);
            return node;
        });
    }
    /**
     * Goes one step forward in the parameter history and applies the parameter values.
     *
     * @returns The tree node returned by the customization function.
     */
    goForward() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.canGoForward()) {
                this._logger.debug(`Session(${this._sessionEngineCore.id}).goForward: Cannot go further forward.`);
                return new viewer_shared_node_tree_1.TreeNode();
            }
            // get the last undone parameter set and apply the values to the parameters
            const lastParameterSet = this._parameterHistoryForward.pop();
            for (const parameterId in lastParameterSet)
                this.parameters[parameterId].value =
                    lastParameterSet[parameterId].value;
            // call the customization function with the parameterHistoryCall value set to true
            this._parameterHistoryCall = true;
            const node = yield this._sessionEngineCore.customizationManager.customize();
            this._parameterHistoryCall = false;
            // add the current parameter set to the history
            this._parameterHistory.push(lastParameterSet);
            return node;
        });
    }
}
exports.ParameterManager = ParameterManager;
//# sourceMappingURL=ParameterManager.js.map